import { BadgeCard, BadgeCardWidth } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { CSSProperties, FC, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMousePosition } from '../../../hooks/useMousePosition';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { PickedCard } from '../types';

export interface CardPreviewProps {
  pickedCard: PickedCard;
  preventEscape: boolean;
}

export const CardPreview: FC<CardPreviewProps> = observer(({ pickedCard, preventEscape }) => {
  const card = pickedCard.card;

  const { x, y } = useMousePosition({ defaultX: pickedCard.position.x, defaultY: pickedCard.position.y });
  const store = useTeachingBlockStore();

  const count = store.freeCards.filter(
    (c) => c.lesson?.uuid === card?.lesson?.uuid && c.duration === card.duration,
  ).length;

  const offsetX = -10;
  const offsetY = -10;

  const style: CSSProperties = {
    position: 'fixed',
    zIndex: 10,
    left: x - offsetX,
    top: y - offsetY,
    opacity: 0.75,
  };

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (!preventEscape && event.key === 'Escape') {
        store.onEscape(card);
      }
    }

    function handleClick(event: MouseEvent) {
      const element = event.target as HTMLElement;
      if (element.classList.contains('drop-and-delete')) {
        if (card.startDate) store.discardCard(card);
        else store.pickCard(null);
      } else if (!element.classList.contains('droppable')) {
        store.pickCard(null);
      } else if ((pickedCard.counter ?? 0) < 1) {
        store.pickCard(null);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [card, store.discardCard, store.pickCard, pickedCard.counter, preventEscape, store]);

  const badgeCardRows = card.badgeCardRows ?? [];

  return createPortal(
    <BadgeCard
      label={card.subject?.shortName ?? ''}
      count={count}
      rows={badgeCardRows}
      width={card.duration as BadgeCardWidth}
      showHover={false}
      showGap={!!badgeCardRows.length}
      showRounded={false}
      style={style}
      color={card.badgeCardTextColor}
    />,
    document.fullscreenElement ? (document.querySelector('#teaching-block-grid') ?? document.body) : document.body,
  );
});
